import React, { useState } from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import {
  ArrowForward,
  AssignmentOutlined,
  BoltOutlined,
  BusinessCenterOutlined,
  ChatBubbleOutline,
  CheckCircleOutline,
  CodeOutlined,
  DescriptionOutlined,
  EqualizerOutlined,
  HistoryOutlined,
  LayersOutlined,
  PsychologyOutlined,
  SchoolOutlined,
  SettingsOutlined,
  TrackChangesOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useUser } from "../context/AuthContext";
import "../editorial.css";

const setupCards = [
  ["01", "Target role", "Select the position you are preparing for or define a custom role.", "REQUIRED", "Shapes the question context", BusinessCenterOutlined],
  ["02", "Interview type", "Choose technical, behavioural, HR, system design, or a mixed interview.", "CORE", "Controls the session structure", LayersOutlined],
  ["03", "Experience level", "Set the expected depth for entry-level through lead roles.", "ADAPTIVE", "Adjusts question depth", SchoolOutlined],
  ["04", "Difficulty", "Choose guided, standard, or challenging interview behaviour.", "ADAPTIVE", "Controls follow-up intensity", BoltOutlined],
  ["05", "Focus areas", "Prioritise communication, reasoning, technical accuracy, and structure.", "SELECT", "Defines the evaluation lens", TrackChangesOutlined],
  ["06", "Optional context", "Add a job description, company context, or specific topics.", "OPTIONAL", "Improves question relevance", DescriptionOutlined],
];

const productLayers = {
  "Interview setup": setupCards,
  "Live interview": [
    ["01", "Text conversation", "Answer naturally while the interviewer guides one focused question at a time.", "TEXT", "Clear prompt and response controls", ChatBubbleOutline],
    ["02", "Question pacing", "Work through one focused question at a time without dashboard noise.", "FOCUSED", "Clear question and response controls", AssignmentOutlined],
    ["03", "Adaptive follow-ups", "Follow-up depth responds to the role, level, and answer context.", "ADAPTIVE", "Keeps the interview relevant", PsychologyOutlined],
    ["04", "Session controls", "Review your answers, move forward, or finish when you have enough evidence.", "CONTROL", "Designed for focused practice", SettingsOutlined],
  ],
  Feedback: [
    ["01", "Response evidence", "Review the question, reasoning, and recommendation behind each answer.", "EVIDENCE", "Feedback stays tied to your answer", DescriptionOutlined],
    ["02", "Communication", "Inspect clarity, structure, and how directly the answer addressed the question.", "REVIEW", "Actionable communication signals", ChatBubbleOutline],
    ["03", "Role knowledge", "See where technical or role-specific depth was strong or incomplete.", "REVIEW", "Aligned with the configured role", CodeOutlined],
    ["04", "Next action", "Turn the most important gap into a concrete practice priority.", "FOCUS", "One clear improvement direction", TrackChangesOutlined],
  ],
  "Improvement plan": [
    ["01", "Priority gaps", "Start with the skills that most affected response quality.", "ORDERED", "Reduces practice ambiguity", TrackChangesOutlined],
    ["02", "Focused practice", "Repeat the interview layer that needs work with updated context.", "PRACTICE", "Keeps preparation relevant", SchoolOutlined],
    ["03", "Review loop", "Use new session evidence to decide what should come next.", "ITERATE", "Connects practice to feedback", EqualizerOutlined],
  ],
  "Interview history": [
    ["01", "Session record", "Return to completed interviews and their generated reports.", "ARCHIVE", "Keeps evidence accessible", HistoryOutlined],
    ["02", "Report review", "Revisit question-level feedback before the next practice session.", "REVIEW", "Supports deliberate repetition", DescriptionOutlined],
    ["03", "Practice continuity", "Carry role and focus context into the next interview setup.", "CONTINUE", "Avoids rebuilding context", ArrowForward],
  ],
};

const modeContent = {
  Technical: ["Coding reasoning", "Technical fundamentals", "Architecture decisions", "Debugging approach"],
  Behavioural: ["Ownership", "Conflict resolution", "Leadership", "Communication"],
  "HR screening": ["Career direction", "Role motivation", "Working preferences", "Experience overview"],
  "System design": ["Requirements", "Architecture", "Trade-offs", "Reliability"],
  Mixed: ["Role fundamentals", "Problem solving", "Communication", "Behavioural evidence"],
};

function SectionHeader({ eyebrow, title, body }) {
  return (
    <Box className="editorial-section-header">
      <Typography component="p" className="editorial-eyebrow">{eyebrow}</Typography>
      <Typography component="h2">{title}</Typography>
      {body && <Typography component="p" className="editorial-lede">{body}</Typography>}
    </Box>
  );
}

function SegmentedNavigation({ items, value, onChange, label }) {
  const handleKeyDown = (event) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    const tabs = Array.from(event.currentTarget.querySelectorAll('[role="tab"]'));
    const current = tabs.indexOf(document.activeElement);
    const next = event.key === "Home"
      ? 0
      : event.key === "End"
        ? tabs.length - 1
        : (current + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
    event.preventDefault();
    tabs[next].focus();
    onChange(items[next]);
  };

  return (
    <Box className="editorial-tabs" role="tablist" aria-label={label} onKeyDown={handleKeyDown}>
      {items.map((item) => (
        <button
          key={item}
          type="button"
          role="tab"
          aria-selected={value === item}
          className={value === item ? "active" : ""}
          onClick={() => onChange(item)}
        >
          {item}
        </button>
      ))}
    </Box>
  );
}

function IconMedallion({ icon: Icon }) {
  return <Box className="editorial-medallion"><Icon /></Box>;
}

function CatalogueCard({ card }) {
  const [number, title, body, status, meta, Icon] = card;
  return (
    <Box component="article" className="catalogue-card">
      <Box className="catalogue-card-top">
        <IconMedallion icon={Icon} />
        <Typography component="span">{number}</Typography>
      </Box>
      <Typography component="h3">{title}</Typography>
      <Typography component="p" className="catalogue-card-copy">{body}</Typography>
      <Box className="catalogue-card-footer">
        <Typography component="span" className="editorial-status">{status}</Typography>
        <Typography component="span">{meta}</Typography>
      </Box>
    </Box>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { isSignedIn } = useUser();
  const startPath = isSignedIn ? "/interviews" : "/interview-config";
  const [activeLayer, setActiveLayer] = useState("Interview setup");
  const [activeMode, setActiveMode] = useState("Technical");

  return (
    <Box className="editorial-page">
      <Navbar />

      <Box component="main">
        <Box component="section" className="editorial-hero">
          <Container maxWidth={false} className="editorial-container">
            <Box className="editorial-hero-grid">
              <Box className="editorial-hero-copy-block">
                <Typography component="p" className="editorial-eyebrow">Interview operating system</Typography>
                <Typography component="h1">
                  One place to prepare, practice, <Box component="span" className="editorial-hero-accent">and improve</Box>
                </Typography>
                <Typography component="p" className="editorial-hero-copy">
                  Configure the interview you want to practise, complete a realistic session, and turn every answer into structured feedback and a focused improvement plan.
                </Typography>
                <Box className="editorial-hero-actions">
                  <Button variant="contained" endIcon={<ArrowForward />} onClick={() => navigate(startPath)}>
                    Start practicing
                  </Button>
                  <Button variant="outlined" onClick={() => navigate("/tracks")}>Explore interview tracks</Button>
                </Box>
              </Box>
              <Box className="editorial-hero-visual" aria-label="Evaluate Yourself interview preview">
                <Box className="editorial-hero-glow" aria-hidden="true" />
                <Box component="figure" className="editorial-hero-frame" sx={{ m: 0 }}>
                  <Box className="editorial-hero-frame-bar" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                    <Typography component="div" className="editorial-hero-frame-label">
                      Evaluate Yourself
                    </Typography>
                  </Box>
                  <Box
                    component="img"
                    src="/assets/skillevaluation.png"
                    alt="Illustration of an interview workspace with a skill evaluation screen"
                  />
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>

        <Container maxWidth={false} className="editorial-container">
          <Box component="section" className="editorial-control-panel" aria-labelledby="system-heading">
            <Box className="editorial-control-intro">
              <IconMedallion icon={SettingsOutlined} />
              <Typography component="p" className="editorial-eyebrow">Interview system · Step 01</Typography>
              <Typography component="h2" id="system-heading">{activeLayer}</Typography>
              <Typography component="p">
                {activeLayer === "Interview setup"
                  ? "Choose the role, interview style, difficulty, and evaluation focus before entering the interview studio."
                  : "Move through a connected practice loop without losing the context from the step before it."}
              </Typography>
              <Typography component="span" className="editorial-count">{productLayers[activeLayer].length} modules</Typography>
            </Box>
            <SegmentedNavigation
              items={Object.keys(productLayers)}
              value={activeLayer}
              onChange={setActiveLayer}
              label="Interview system sections"
            />
          </Box>

          <Box className="catalogue-grid" aria-live="polite">
            {productLayers[activeLayer].map((card) => <CatalogueCard key={card[1]} card={card} />)}
          </Box>

          <Box component="section" className="editorial-section" id="features">
            <SectionHeader
              eyebrow="Workflow"
              title="From setup to measurable improvement"
              body="Each stage reduces ambiguity and shows exactly what to practise next."
            />
            <Box className="workflow-panel">
              {[
                ["01", "Configure", "Define role, format, and goals."],
                ["02", "Practice", "Complete one focused question at a time."],
                ["03", "Review", "Inspect evidence-backed feedback."],
                ["04", "Improve", "Follow the highest-priority practice gap."],
              ].map(([number, title, body]) => (
                <Box key={title} className="workflow-step">
                  <Typography component="span">{number}</Typography>
                  <Typography component="h3">{title}</Typography>
                  <Typography component="p">{body}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box component="section" className="editorial-section" id="tracks">
            <SectionHeader eyebrow="Interview layers" title="Choose the interview layer you need" />
            <Box className="mode-panel">
              <SegmentedNavigation
                items={Object.keys(modeContent)}
                value={activeMode}
                onChange={setActiveMode}
                label="Interview modes"
              />
              <Box className="mode-list" aria-live="polite">
                {modeContent[activeMode].map((item, index) => (
                  <Box key={item} className="mode-list-item">
                    <Typography component="span">{String(index + 1).padStart(2, "0")}</Typography>
                    <Typography component="h3">{item}</Typography>
                    <CheckCircleOutline aria-hidden="true" />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Box component="section" className="editorial-section" id="resources">
            <SectionHeader
              eyebrow="Feedback"
              title="See how every answer performed"
              body="The report keeps recommendations connected to the question and answer evidence that produced them."
            />
            <Box className="feedback-preview">
              <Box className="feedback-evidence">
                <Typography component="p" className="editorial-eyebrow">Question evidence</Typography>
                <Typography component="h3">Question and response review</Typography>
                <Typography component="p">
                  After the interview, the question and the evidence behind your feedback appear here for review.
                </Typography>
                <Box className="feedback-answer-evidence">
                  <Typography component="span">Answer evidence</Typography>
                  <Typography component="p">Your response evidence is shown with the feedback it supports.</Typography>
                </Box>
              </Box>
              <Box className="feedback-review">
                <Typography component="p" className="editorial-eyebrow">Evaluation structure</Typography>
                {["Communication", "Response structure", "Relevance", "Reasoning", "Role knowledge"].map((item) => (
                  <Box key={item} className="feedback-row">
                    <Typography component="span">{item}</Typography>
                    <Typography component="strong">Reviewed per response</Typography>
                  </Box>
                ))}
                <Box className="feedback-recommendation">
                  <Typography component="span">Focused recommendation</Typography>
                  <Typography component="p">One concrete next step closes each review.</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box component="section" className="editorial-final-cta">
            <Box>
              <Typography component="p" className="editorial-eyebrow">Ready to practise</Typography>
              <Typography component="h2">Build an interview around your next opportunity</Typography>
              <Typography component="p">Choose the role, set the level, and start a focused practice session in minutes.</Typography>
            </Box>
            <Box className="editorial-cta-actions">
              <Button variant="contained" endIcon={<ArrowForward />} onClick={() => navigate(startPath)}>
                Configure interview
              </Button>
              <Button variant="outlined" onClick={() => navigate("/tracks")}>Explore interview tracks</Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
